import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

type DialogOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  defaultValue?: string;
  type: 'confirm' | 'prompt';
};

type DialogState = DialogOptions & {
  resolve: (value: any) => void;
};

interface DialogContextType {
  confirm: (message: string, title?: string) => Promise<boolean>;
  prompt: (message: string, defaultValue?: string, title?: string) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useDialog deve ser usado dentro de um DialogProvider');
  return context;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const confirm = useCallback((message: string, title = 'Confirmação') => {
    return new Promise<boolean>((resolve) => {
      setDialog({ type: 'confirm', message, title, resolve, confirmText: 'Confirmar', cancelText: 'Cancelar' });
    });
  }, []);

  const prompt = useCallback((message: string, defaultValue = '', title = 'Entrada de Dados') => {
    setInputValue(defaultValue);
    return new Promise<string | null>((resolve) => {
      setDialog({ type: 'prompt', message, title, defaultValue, resolve, confirmText: 'OK', cancelText: 'Cancelar' });
    });
  }, []);

  const handleConfirm = () => {
    if (dialog?.type === 'prompt') {
      dialog.resolve(inputValue);
    } else {
      dialog?.resolve(true);
    }
    setDialog(null);
  };

  const handleCancel = () => {
    if (dialog?.type === 'prompt') {
      dialog.resolve(null);
    } else {
      dialog?.resolve(false);
    }
    setDialog(null);
  };

  useEffect(() => {
    if (dialog?.type === 'prompt' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [dialog]);

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}
      {dialog && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-conteudo" style={{ maxWidth: '450px' }}>
            <h3>{dialog.title}</h3>
            <p style={{ marginBottom: '1.5rem', whiteSpace: 'pre-wrap' }}>{dialog.message}</p>

            {dialog.type === 'prompt' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleConfirm();
                    if (e.key === 'Escape') handleCancel();
                  }}
                  autoFocus
                />
              </div>
            )}

            <div className="form-actions" style={{ gap: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="botao botao-secundario" onClick={handleCancel}>
                {dialog.cancelText}
              </button>
              <button className="botao botao-primario" onClick={handleConfirm}>
                {dialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};
